/**
 * Labels for the shared company picker when it is used as a vendor#-first
 * assignment control (Item Master pricelists).
 */

/**
 * Vendor / company names on Pricelist Management surfaces are forced to caps
 * (GEN-2567). Display and the price-file log only — the company record is
 * not rewritten.
 */
export function pricelistVendorName(name: string): string {
  return name.toUpperCase();
}

// Shared with the backend export (the log's "3 LETTER CODE / PG" default
// reads the same rule), so it lives in @erp/shared-utils.
export { displayCompanyCode } from '@erp/shared-utils';

export function formatVendorNumbers(
  vendorNumbers: readonly string[] | undefined,
): string {
  return (vendorNumbers ?? [])
    .filter((value) => value.trim() !== '')
    .join(', ');
}

export function listedVendorNumbers(
  vendorNumbers: readonly string[] | undefined,
): string[] {
  return (vendorNumbers ?? []).filter((value) => value.trim() !== '');
}

export function explodePricelistPickerCompanies<
  T extends { vendorNumbers: readonly string[] },
>(companies: readonly T[]): T[] {
  return companies.flatMap((company) => {
    const vendorNumbers = listedVendorNumbers(company.vendorNumbers);
    if (vendorNumbers.length <= 1) {
      return [company];
    }
    return vendorNumbers.map((vendorNumber) => ({
      ...company,
      vendorNumbers: [vendorNumber],
    }));
  });
}

const PREFIXED_VENDOR_NUMBER = /^[a-z]{0,8}(\d{5,})$/;

/**
 * The typed term plus its 5-digit vendor#, mirroring the server's
 * `extractFiveDigitVendorNumber`: the last `:` segment, then bare digits or a
 * letter prefix (`61500`, `RA61500`, `RA:61500`, `RA:RA61500`).
 */
function vendorSearchNeedles(search: string): string[] {
  const trimmed = search.trim().toLowerCase();
  if (trimmed === '') return [];
  const segment = trimmed.slice(trimmed.lastIndexOf(':') + 1);
  const extracted = PREFIXED_VENDOR_NUMBER.exec(segment)?.[1];
  return extracted === undefined ? [trimmed] : [trimmed, extracted];
}

/** 0 = exact vendor#, 1 = vendor# prefix, 2 = name / code / other. */
function pricelistPickerOptionRank(
  vendorNumbers: readonly string[],
  needles: ReadonlySet<string>,
): number {
  const numbers = listedVendorNumbers(vendorNumbers).map((value) =>
    value.trim().toLowerCase(),
  );
  if (numbers.some((value) => needles.has(value))) return 0;
  const needleList = [...needles];
  if (numbers.some((value) => needleList.some((n) => value.startsWith(n)))) {
    return 1;
  }
  return 2;
}

/**
 * GEN-3073: after one company explodes into one option per vendor#, lead
 * with the exact vendor# typed, then prefixes, then the rest. Stable, so
 * the server's order holds within each tier.
 */
export function rankPricelistPickerOptions<
  T extends { vendorNumbers: readonly string[] },
>(options: readonly T[], search: string): T[] {
  const needles = new Set(vendorSearchNeedles(search));
  if (needles.size === 0) return [...options];
  return options
    .map((option) => ({
      option,
      rank: pricelistPickerOptionRank(option.vendorNumbers, needles),
    }))
    .sort((left, right) => left.rank - right.rank)
    .map(({ option }) => option);
}

export function pricelistPickerOptionKey(company: {
  id: string;
  vendorNumbers: readonly string[];
}): string {
  const vendorNumber = company.vendorNumbers[0];
  return vendorNumber === undefined
    ? company.id
    : `${company.id}:${vendorNumber}`;
}

/**
 * The picker value for a company option. Item Master lists one option per
 * vendor#, so the vendor# is part of the key there; elsewhere the company id is.
 */
export function companyPickerValue(
  company: { id: string; vendorNumbers?: readonly string[] },
  vendorFirst: boolean,
): string {
  return vendorFirst
    ? pricelistPickerOptionKey({
        id: company.id,
        vendorNumbers: company.vendorNumbers ?? [],
      })
    : company.id;
}

export function companyPickerTriggerLabel(
  company: { name: string; vendorNumbers?: readonly string[] } | null,
  fallback: string,
  vendorFirst = false,
): string {
  if (company === null) return fallback;
  const name = vendorFirst ? pricelistVendorName(company.name) : company.name;
  if (!vendorFirst) return name;
  const vendorLabel = formatVendorNumbers(company.vendorNumbers);
  return vendorLabel === '' ? name : `${vendorLabel} — ${name}`;
}

export function companyOptionPrimary(company: {
  name: string;
  vendorNumbers: readonly string[];
}): string {
  return (
    formatVendorNumbers(company.vendorNumbers) ||
    pricelistVendorName(company.name)
  );
}
